import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  PALETTE_PRESETS,
  DEFAULT_PRESET_ID,
  generateScale,
  ensureUsable,
  type ColorScale,
  type BgTone,
} from '@softfactura/ui';
import {
  type TableHeaderStyle,
  DEFAULT_TABLE_HEADER_STYLE,
} from '@/shared/constants/table-header-styles';

export type HeaderStyle = 'minimal' | 'gradient-radial' | 'gradient-linear' | 'solid-bar' | 'outline' | 'clean';
export type CornerStyle = 'rounded' | 'square';

export const MONO_PRESET_ID = 'mono';

interface ThemePrefsStore {
  presetId: string;
  customHex: string | null;
  customOriginalHex: string | null;
  scale: ColorScale;
  headerStyle: HeaderStyle;
  bgTone: BgTone;
  cornerStyle: CornerStyle;
  tableHeaderStyle: TableHeaderStyle;
  setPreset: (id: string) => void;
  setCustomColor: (hex: string) => void;
  setHeaderStyle: (style: HeaderStyle) => void;
  setBgTone: (tone: BgTone) => void;
  setCornerStyle: (style: CornerStyle) => void;
  setTableHeaderStyle: (style: TableHeaderStyle) => void;
  reset: () => void;
}

const DEFAULT_SCALE = PALETTE_PRESETS[DEFAULT_PRESET_ID].scale;

export const useThemePrefsStore = create<ThemePrefsStore>()(
  persist(
    (set) => ({
      presetId: DEFAULT_PRESET_ID,
      customHex: null,
      customOriginalHex: null,
      scale: DEFAULT_SCALE,
      headerStyle: 'minimal',
      bgTone: 'auto',
      cornerStyle: 'rounded',
      tableHeaderStyle: DEFAULT_TABLE_HEADER_STYLE,
      setPreset: (id) => {
        const preset = PALETTE_PRESETS[id];
        if (!preset) return;
        if (id === MONO_PRESET_ID) {
          // Composite: presetul „Curat" comută simultan paleta, header style
          // si fundalul secundar la valori neutre — produce experienta
          // promisa in pagina Aspect (fara culori, fara gradient, fara bg).
          set({
            presetId: id,
            customHex: null,
            customOriginalHex: null,
            scale: preset.scale,
            headerStyle: 'clean',
            bgTone: 'neutral',
          });
          return;
        }
        set({ presetId: id, customHex: null, customOriginalHex: null, scale: preset.scale });
      },
      setCustomColor: (hex) => {
        const normalized = hex.startsWith('#') ? hex.toUpperCase() : `#${hex.toUpperCase()}`;
        const usable = ensureUsable(normalized);
        set({
          presetId: 'custom',
          customHex: usable.hex,
          customOriginalHex: usable.adjusted ? usable.originalHex : null,
          scale: generateScale(usable.hex),
        });
      },
      setHeaderStyle: (style) => set({ headerStyle: style }),
      setBgTone: (tone) => set({ bgTone: tone }),
      setCornerStyle: (style) => set({ cornerStyle: style }),
      setTableHeaderStyle: (style) => set({ tableHeaderStyle: style }),
      reset: () => set({
        presetId: DEFAULT_PRESET_ID,
        customHex: null,
        customOriginalHex: null,
        scale: DEFAULT_SCALE,
        headerStyle: 'minimal',
        bgTone: 'auto',
        cornerStyle: 'rounded',
        tableHeaderStyle: DEFAULT_TABLE_HEADER_STYLE,
      }),
    }),
    {
      name: 'sf-theme-prefs',
      storage: createJSONStorage(() => (typeof window !== 'undefined' ? localStorage : ({
        getItem: () => null, setItem: () => {}, removeItem: () => {},
      } as Storage))),
      partialize: (state) => ({
        presetId: state.presetId,
        customHex: state.customHex,
        customOriginalHex: state.customOriginalHex,
        scale: state.scale,
        headerStyle: state.headerStyle,
        bgTone: state.bgTone,
        cornerStyle: state.cornerStyle,
        tableHeaderStyle: state.tableHeaderStyle,
      }),
    }
  )
);
